import { useContext, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStatsigClient } from '@statsig/react-bindings';

import LogRocket from 'logrocket';
import PropTypes from 'prop-types';

import {
  ERROR_SAVE_DRAFT,
  RESEARCH_OBJECTIVES_MAX_LENGTH,
  QUESTION_TYPES,
} from '@api/instant_survey';

import { createAtlasSurvey } from '@api/surveys';
import { GATES } from '@constants';
import {
  ButtonText,
  Card,
  HelpIcon,
  Input,
  MinusIcon,
  OverlayTriggerTooltip,
  Pill,
  PlusIcon,
  SaveIcon,
  Stepper,
  TextArea,
  toast,
  Toolbar,
} from '@utilities';

import { getCurrentSamplePrice } from '../../utilities/helpers';
import { payWithCompletes, payWithCredits } from '../../../Checkout/helpers/constants';

import ModalSaveAsDraft from '../ModalSaveAsDraft';
import ModalTitleRequired from '../ModalTitleRequired';
import { SurveyContext } from '../../utilities/context';

import ModalLaunchMethodType from './components/ModalLaunchMethodType';

import styles from './_index.module.scss';

const SurveyToolbar = ({ isSaveAsDraftDisabled, tools = [] }) => {
  const {
    activeGroups,
    activeQuestions,
    activeStep,
    currentQuestionCount,
    currentQuestionnairePrice,
    insightsDocument,
    insightsDocumentType,
    instantSurveyType,
    isLoadingSubscription,
    isResearchObjectivesTooLong,
    isSaveAsDraftOpen,
    isSavingAsDraft,
    isSubscribed,
    launchMethod,
    maxQuestions,
    saveInstantSurvey,
    setActiveStep,
    setIsSaveAsDraftOpen,
    setIsSavingAsDraft,
    setLaunchMethod,
    subscription,
    subscriptionError,
    survey,
    updateSurvey,
  } = useContext(SurveyContext);
  const [isLaunchMethodModalOpen, setIsLaunchMethodModalOpen] = useState(false);

  const [isSurveyTitleModalOpen, setIsSurveyTitleModalOpen] = useState(false);
  const [showResearchObjectiveEdit, setShowResearchObjectiveEdit] = useState(false);
  const { client } = useStatsigClient();
  const canShowResearchObjectives = client.checkGate(GATES.RESEARCH_OBJECTIVES);

  const currentSamplePrice = useMemo(
    () =>
      getCurrentSamplePrice({
        samplePricing: insightsDocumentType?.samplePricing,
        survey,
      }),
    [survey]
  );

  const handleConfirmMethod = (method) => {
    setLaunchMethod(method);
    setIsLaunchMethodModalOpen(false);
  };

  const currentTotalResponses = activeGroups.reduce((acc, group) => acc + group.quotaLimit, 0);
  const hasMaxDiffQuestion = activeQuestions.some(
    ({ type }) => type === QUESTION_TYPES.MAX_DIFF.value
  );
  const isTurfSurvey = instantSurveyType?.id === 'turf-v3-instant-survey-type';
  const isSubscriptionAvailable =
    process.env.REACT_APP_LAUNCH_METHOD &&
    isSubscribed &&
    !isLoadingSubscription &&
    !subscriptionError &&
    subscription?.hasStarted === true;

  const advancedMethodologyCompletesCost = 300;
  const advancedMethodologyCreditCost = 1;
  const completesTotal =
    currentTotalResponses +
    (currentQuestionnairePrice?.questions >= 16 ? 100 : 0) +
    (hasMaxDiffQuestion ? advancedMethodologyCompletesCost : 0) +
    (isTurfSurvey ? advancedMethodologyCompletesCost : 0);
  const creditsTotal =
    (currentSamplePrice?.amount ?? 0) +
    (currentQuestionnairePrice?.amount ?? 1) +
    (hasMaxDiffQuestion ? advancedMethodologyCreditCost : 0) +
    (isTurfSurvey ? advancedMethodologyCreditCost : 0);

  const navigate = useNavigate();
  const handleOpenSaveAsDraft = () => {
    if (!survey.title.trim()) {
      setIsSurveyTitleModalOpen(true);
    } else {
      setIsSaveAsDraftOpen(true);
    }
  };
  const handleSaveAndExit = async () => {
    setIsSavingAsDraft(true);
    try {
      const response = await saveInstantSurvey();
      await createAtlasSurvey({
        atlasInstantSurveyRef: response.id,
        id: insightsDocument?.id,
        paymentMethod: launchMethod,
        status: 'draft',
        title: survey.title,
      });
    } catch (error) {
      toast(ERROR_SAVE_DRAFT, { status: 'error' });
      setIsSavingAsDraft(false);
      return;
    }

    LogRocket.track('InstantSurvey_SaveAsDraft', {
      country: survey.country,
      groups: survey.groups.length,
      questions: survey.questions.length,
      ...(survey.surveyTemplateId && { template: survey.surveyTemplateId }),
      type: instantSurveyType.id,
    });
    navigate({
      pathname: '/dashboard/workspace/surveys',
      search: new URLSearchParams({ filters: 'is_draft_item' }).toString(),
    });
  };

  return (
    <>
      <Card className={styles['survey-toolbar']} padding="medium">
        <div>
          <Toolbar
            tools={[
              ...tools,
              <OverlayTriggerTooltip content="Save Draft" key="save-as-draft">
                <button
                  data-testid="save-draft-button"
                  disabled={isSaveAsDraftDisabled}
                  onClick={handleOpenSaveAsDraft}
                >
                  <SaveIcon />
                </button>
              </OverlayTriggerTooltip>,
              ...(instantSurveyType.helpUrl
                ? [
                    <OverlayTriggerTooltip aria-label="Help" content="Help" key="help">
                      <a
                        data-testid="help-link-button"
                        href={instantSurveyType.helpUrl}
                        rel="noreferrer"
                        target="_blank"
                      >
                        <HelpIcon />
                      </a>
                    </OverlayTriggerTooltip>,
                  ]
                : []),
            ]}
          >
            <Input
              className={styles['survey-toolbar-input']}
              maxLength={255}
              onBlur={(title) => updateSurvey({ title: title.trim() })}
              onChange={(title) => updateSurvey({ title })}
              placeholder="Name your survey (e.g., Heavy vs Light Walmart coffee buyers)"
              value={survey.title}
            />
          </Toolbar>
          {canShowResearchObjectives && (
            <div className={styles['survey-toolbar-research-objectives']}>
              {showResearchObjectiveEdit || survey.researchObjectives ? (
                <div>
                  <div className={styles['survey-toolbar-research-objectives-edit']}>
                    <TextArea
                      className={styles['survey-toolbar-research-objectives-textarea']}
                      error={
                        isResearchObjectivesTooLong
                          ? `Research objective is too long, cannot exceed ${RESEARCH_OBJECTIVES_MAX_LENGTH} characters.`
                          : null
                      }
                      onBlur={() => {
                        updateSurvey({ researchObjectives: survey.researchObjectives?.trim() });
                        setShowResearchObjectiveEdit(false);
                      }}
                      onChange={(research_objectives) =>
                        updateSurvey({ researchObjectives: research_objectives })
                      }
                      placeholder="Enter the research objective for this survey."
                      value={survey.researchObjectives || ''}
                    />
                  </div>
                  <div>
                    <ButtonText
                      onClick={() => {
                        updateSurvey({ researchObjectives: '' });
                        setShowResearchObjectiveEdit(false);
                      }}
                    >
                      <MinusIcon />
                      <span>
                        Remove <strong>Objective</strong>
                      </span>
                    </ButtonText>
                  </div>
                </div>
              ) : (
                <ButtonText onClick={() => setShowResearchObjectiveEdit(true)}>
                  <PlusIcon />
                  <span>
                    Add <strong>Objective</strong>
                  </span>
                </ButtonText>
              )}
            </div>
          )}
        </div>

        <div className={styles['survey-toolbar-info']}>
          <div>
            <p>
              <strong>{activeGroups.length}</strong> Quota Group
              {activeGroups.length > 1 ? 's' : ''}
            </p>
            <p>
              <strong>{currentTotalResponses}</strong> Total Responses
            </p>
            <p>
              <strong>{currentQuestionCount + '/' + maxQuestions}</strong> Questions
            </p>
          </div>
          <div>
            {isSubscriptionAvailable ? (
              <>
                <button
                  className={styles['survey-toolbar-launch-method-button']}
                  onClick={() => setIsLaunchMethodModalOpen(true)}
                >
                  Launch with
                </button>
                &nbsp;
                <span>
                  <strong>
                    {launchMethod === payWithCompletes.key
                      ? `${completesTotal} Completes`
                      : launchMethod === payWithCredits.key &&
                          creditsTotal !== undefined &&
                          !isNaN(creditsTotal)
                        ? `${creditsTotal} Credit${creditsTotal === 1 ? '' : 's'}`
                        : 'N/A'}
                  </strong>
                </span>
              </>
            ) : (
              <>
                Total Cost:&nbsp;
                {currentSamplePrice ? (
                  <Pill
                    isCompleted
                    value={`${creditsTotal} Credit${creditsTotal === 1 ? '' : 's'}`}
                  />
                ) : (
                  <span>N/A</span>
                )}
              </>
            )}
          </div>
        </div>
        <Stepper
          activeIndex={activeStep}
          navigationType="linear"
          onChange={(index) => setActiveStep(index)}
          steps={[
            {
              isComplete: activeStep > 0,
              label: 'Select Panelists',
            },
            {
              isComplete: survey.questions.length >= 1,
              label: 'Create Questionnaire',
            },
            {
              label: 'Review Survey',
            },
          ]}
        />
      </Card>
      {isSaveAsDraftOpen && (
        <ModalSaveAsDraft
          isActive
          isSaving={isSavingAsDraft}
          onClose={() => setIsSaveAsDraftOpen(false)}
          onSubmit={handleSaveAndExit}
        />
      )}
      {isSurveyTitleModalOpen && (
        <ModalTitleRequired onClose={() => setIsSurveyTitleModalOpen(false)} />
      )}
      {isLaunchMethodModalOpen && (
        <ModalLaunchMethodType
          creditsTotal={creditsTotal}
          isOpen={isLaunchMethodModalOpen}
          launchMethod={launchMethod}
          onClose={() => setIsLaunchMethodModalOpen(false)}
          onConfirm={handleConfirmMethod}
          setLaunchMethod={setLaunchMethod}
          subscription={subscription}
        />
      )}
    </>
  );
};

SurveyToolbar.propTypes = {
  isSaveAsDraftDisabled: PropTypes.bool,
  tools: PropTypes.array,
};

export default SurveyToolbar;
